'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

// Helper function to render text with [bold] syntax
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\[bold][^[]*?\[bold])/g);

  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/\[bold]([^[]*?)\[bold]/);
        if (match) {
          return (
            <strong key={i} className="font-bold">
              {match[1]}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

// Helper function to render text with bulletpoints
const renderTextWithBulletpoints = (text: string) => {
  // Check if text contains [Bulletpoint] keyword
  if (text.includes('[Bulletpoint]')) {
    // Split by [Bulletpoint] to separate intro text and bullet items
    const parts = text.split('[Bulletpoint]');
    const introText = parts[0].trim(); // First part is the intro paragraph
    const bulletItems = parts.slice(1).filter((part) => part.trim()); // Rest are bullets

    return (
      <>
        {/* Render intro paragraph if exists */}
        {introText && (
          <p className="text-[18px] text-white leading-[1.4] font-normal font-inter mb-4">
            {renderTextWithBold(introText)}
          </p>
        )}

        {/* Render bullet list */}
        {bulletItems.length > 0 && (
          <ul className="list-disc pl-0 space-y-3">
            {bulletItems.map((part, index) => {
              const trimmedPart = part.trim();
              return (
                <li
                  key={index}
                  className="text-[18px] leading-[1.4] font-normal font-inter ml-[27px] mb-3 last:mb-0"
                >
                  {renderTextWithBold(trimmedPart)}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  // If no bulletpoints, just return the text
  return (
    <p className="text-[18px] text-white leading-[1.4] font-normal font-inter">
      {text}
    </p>
  );
};

/**
 * Props for `ImpactHighlights2`.
 */
export type ImpactHighlights2Props =
  SliceComponentProps<Content.ImpactHighlights2Slice>;

/**
 * Component for "ImpactHighlights2" Slices.
 */
const ImpactHighlights2: FC<ImpactHighlights2Props> = ({ slice }) => {
  const { title, description, highlighted_points, impact_title, impact_list } =
    slice.primary;

  const titleRef = useRef<HTMLDivElement>(null);
  const highlightRefs = useRef<(HTMLDivElement | null)[]>([]);
  const impactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-right');
          entry.target.classList.remove('opacity-0', 'translate-x-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    // Observe title
    if (titleRef.current) {
      const element = titleRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-x-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    // Observe highlights with staggered delay
    highlightRefs.current.forEach((highlightEl, index) => {
      if (highlightEl) {
        currentElements.push(highlightEl);
        highlightEl.classList.add(
          'opacity-0',
          'translate-x-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        highlightEl.style.transitionDelay = `${(index + 1) * 150}ms`;
        observer.observe(highlightEl);
      }
    });

    // Observe impact section
    if (impactRef.current) {
      const element = impactRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-x-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      const highlightCount = highlighted_points?.length || 0;
      element.style.transitionDelay = `${(highlightCount + 1) * 150}ms`;
      observer.observe(element);
    }

    return () => {
      currentElements.forEach((element) => {
        if (element) observer.unobserve(element);
      });
    };
  }, [highlighted_points]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-24"
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F3932 41.42%)',
      }}
    >
      <div className="container mx-auto px-6 lg:px-[200px]">
        <div className="flex flex-col gap-12 md:gap-14 lg:gap-[60px]">
          {/* Top Section: Title, Description, and Highlighted Points */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-6">
            {/* Left: Title and Description */}
            <div ref={titleRef} className="flex-1 flex flex-col gap-6 lg:gap-6">
              {title && (
                <div>
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="text-3xl md:text-[40px] font-medium text-white leading-[1.2] font-inter">
                          {children}
                        </h2>
                      ),
                    }}
                  />
                </div>
              )}

              {description && (
                <div>
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-[18px] text-white leading-[1.5] font-normal font-inter">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-bold">{children}</strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Right: Highlighted Points */}
            {highlighted_points && highlighted_points.length > 0 && (
              <div className="lg:w-[650px] flex flex-col">
                {highlighted_points.map((point, index) => {
                  const isLastItem = index === highlighted_points.length - 1;

                  return (
                    <div key={index}>
                      <div
                        ref={(el) => {
                          highlightRefs.current[index] = el;
                        }}
                        className="w-full rounded-2xl border border-[rgba(5,242,115,0.4)] p-6 lg:p-6 backdrop-blur-[10px]"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0) 100%)',
                        }}
                      >
                        {point.highlight_title && (
                          <h3 className="text-[#05F273] text-xl lg:text-[24px] font-bold leading-[1.4] font-inter mb-2">
                            {point.highlight_title}
                          </h3>
                        )}

                        {point.highlight_description && (
                          <p className="text-white text-[18px] leading-[1.4] font-normal font-inter">
                            {point.highlight_description}
                          </p>
                        )}
                      </div>

                      {/* Connector Line */}
                      {!isLastItem && (
                        <div className="flex items-center justify-center h-[30px] w-full">
                          <svg
                            width="2"
                            height="30"
                            viewBox="0 0 2 30"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <line
                              x1="1"
                              y1="0"
                              x2="1"
                              y2="30"
                              stroke="#05F273"
                              strokeWidth="2"
                              strokeDasharray="4 4"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Section: Key Impact */}
          {(impact_title || impact_list) && (
            <div ref={impactRef} className="flex flex-col gap-6">
              <div
                className="rounded-2xl border border-[rgba(5,242,115,0.4)] p-6 lg:p-6 backdrop-blur-[10px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0) 100%)',
                }}
              >
                <div className="flex flex-col gap-6">
                  {impact_title && (
                    <div>
                      <PrismicRichText
                        field={impact_title}
                        components={{
                          heading3: ({ children }) => (
                            <h3 className="text-white text-3xl lg:text-[42px] font-medium leading-[1.2] font-inter">
                              {children}
                            </h3>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {impact_list && (
                    <div className="text-white">
                      {renderTextWithBulletpoints(asText(impact_list) || '')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ImpactHighlights2;
